import { Check, Info } from '../../../../modules/design-system';
import type {
  IframePoolShippingPosition,
  PoolShippingService,
} from '../../../../modules/api';

export interface ServiceSectionProps {
  service: PoolShippingService | null;
  positions: IframePoolShippingPosition[];
}

/**
 * "Delivery Shipping Options" section on the Show page — the chosen
 * service in a rounded card, with paid positions (insurance, etc) as
 * thin rows below. Matches Figma 361:132067.
 */
export function ServiceSection({ service, positions }: ServiceSectionProps) {
  if (!service) return null;

  return (
    <section>
      <h3
        className="text-[17px] font-bold tracking-[-0.3px]"
        style={{ color: 'var(--co-MyPacksItemCard-titleColor, #FFFFFF)' }}
      >
        Delivery Shipping Options
      </h3>

      <div
        className="mt-3 overflow-hidden rounded-[16px]"
        style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <div className="flex items-start justify-between gap-3 p-3">
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-semibold text-white">
              {service.name}
            </div>
            {service.description && (
              <div
                className="mt-1 text-[12px] leading-[16px]"
                style={{ color: 'rgba(255,255,255,0.55)' }}
              >
                {service.description}
              </div>
            )}
          </div>
          {service.price !== null && (
            <div className="shrink-0 text-[14px] font-semibold tabular-nums text-white">
              ${service.price.toFixed(2)}
            </div>
          )}
        </div>

        {positions.map((p) => (
          <div
            key={p.id}
            className="flex items-center justify-between gap-3 px-3 py-3"
            style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div className="flex min-w-0 items-center gap-2 text-[14px] text-white">
              <Check size={16} strokeWidth={2} />
              <span>{p.name}</span>
            </div>
            {p.price !== null && (
              <div className="shrink-0 text-[14px] font-semibold tabular-nums text-white">
                ${p.price.toFixed(2)}
              </div>
            )}
          </div>
        ))}
      </div>

      <div
        className="mt-3 flex items-center gap-2 text-[12px]"
        style={{ color: 'rgba(255,255,255,0.45)' }}
      >
        <Info size={16} strokeWidth={1.6} />
        <span>Shipping costs include payment processing fee</span>
      </div>
    </section>
  );
}
